// Enhanced Twee parser and converter for Visual Story Editor
// Supports planner metadata in HTML comments

/**
 * Node Style Types:
 * - 'standard' : Dark background (default for pages, sections)
 * - 'light'    : 30% lighter background with dark text (for H1, H2 headings)
 * - 'unbounded': No bounding box/background (for direct URL links)
 */
export const NODE_STYLES = {
  STANDARD: 'standard',
  LIGHT: 'light',
  UNBOUNDED: 'unbounded'
};

/**
 * Map nodeLevel to nodeStyle
 */
export const getNodeStyleFromLevel = (nodeLevel, isDirectUrl = false) => {
  if (isDirectUrl) return NODE_STYLES.UNBOUNDED;
  
  switch (nodeLevel) {
    case 'h1':
    case 'h2':
      return NODE_STYLES.LIGHT;
    case 'directUrl':
      return NODE_STYLES.UNBOUNDED;
    case 'page':
    case 'section':
    case 'post':
    case 'category':
    case 'tag':
    case 'root':
    default:
      return NODE_STYLES.STANDARD;
  }
};

/**
 * Parse Twee text into a graph structure
 * Extracts planner metadata from HTML comments and chapter tags
 * @param {string} tweeText - Raw Twee format text
 * @returns {Object} Graph with nodes, edges, and metadata
 */
export const parseTweeToGraph = (tweeText) => {
  const passages = [];
  const lines = tweeText.split('\n');
  let currentPassage = null;
  let storyMetadata = null;
  
  // Parse passages
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.trim().startsWith(':: ')) {
      if (currentPassage && currentPassage.content.trim()) {
        passages.push(currentPassage);
      }
      
      // Extract passage name and tags after ":: " prefix
      const headerContent = line.trim().substring(3);
      const tagMatch = headerContent.match(/^([^\[]+)(?:\[([^\]]+)\])?/);
      
      const name = tagMatch ? tagMatch[1].trim() : headerContent.trim();
      const tags = tagMatch && tagMatch[2] ? tagMatch[2].split(' ').filter(t => t) : [];
      
      // Extract chapter from tags (e.g., [chapter-1])
      const chapterTag = tags.find(t => t.startsWith('chapter-'));
      
      currentPassage = { 
        name, 
        content: '', 
        tags,
        chapterId: chapterTag || null
      };
    } else if (currentPassage) {
      currentPassage.content += line + '\n';
    }
  }
  
  if (currentPassage && currentPassage.content.trim()) {
    passages.push(currentPassage);
  }
  
  // Filter out StoryTitle and StoryData
  const storyPassages = passages.filter(p => p.name !== 'StoryTitle' && p.name !== 'StoryData');
  const storyTitle = passages.find(p => p.name === 'StoryTitle');
  const storyData = passages.find(p => p.name === 'StoryData');
  
  // Parse story metadata from StoryData passage
  if (storyData) {
    storyMetadata = extractPlannerMetadata(storyData.content);
  }
  
  // Create nodes with planner metadata
  const nodes = storyPassages.map((passage, index) => {
    const metadata = extractPlannerMetadata(passage.content);
    const cleanBody = removePlannerMetadata(passage.content);
    
    // Use displayName from metadata if available, otherwise use passage name
    const displayName = metadata.displayname || metadata.displayName || passage.name;
    
    // Determine nodeLevel and isDirectUrl
    const nodeLevel = metadata.nodelevel || metadata.nodeLevel || 'page';
    const isDirectUrl = metadata.isdirecturl === 'true' || metadata.isDirectUrl === 'true';
    
    // Get nodeStyle from metadata or derive from nodeLevel
    const nodeStyle = metadata.nodestyle || metadata.nodeStyle || getNodeStyleFromLevel(nodeLevel, isDirectUrl);
    
    return {
      id: passage.name,
      name: displayName, // Display name shown in UI
      passageId: passage.name, // Original passage ID for linking
      body: cleanBody.trim(),
      x: 0,
      y: 0,
      // Planner fields
      chapterId: passage.chapterId || 'chapter-1',
      intention: metadata.intention || '',
      purpose: metadata.purpose || '',
      dilemma: metadata.dilemma || '',
      // PathNav fields
      url: metadata.url || '',
      nodeLevel: nodeLevel,
      isDirectUrl: isDirectUrl,
      nodeStyle: nodeStyle, // 'standard', 'light', or 'unbounded'
      displayName: displayName
    };
  });
  
  // Extract edges (links between passages)
  const edges = [];
  
  storyPassages.forEach(passage => {
    const links = extractLinks(passage.content);
    links.forEach(link => {
      const targetNode = nodes.find(n => n.id === link.target);
      if (targetNode) {
        edges.push({
          source: passage.name,
          target: link.target,
          label: link.label
        });
      }
    });
  });
  
  // Parse editorConfig from storyMetadata if it exists
  let editorConfig = null;
  if (storyMetadata?.editorConfig || storyMetadata?.editorconfig) {
    try {
      const configStr = storyMetadata.editorConfig || storyMetadata.editorconfig;
      editorConfig = JSON.parse(configStr);
    } catch (e) {
      console.warn('Failed to parse editorConfig:', e);
    }
  }
  
  return {
    nodes,
    edges,
    storyTitle: storyTitle?.content.trim() || storyMetadata?.storyTitle || 'Interactive Story',
    // Story fundamentals (Planner)
    storyVision: storyMetadata?.vision || '',
    storyChallenges: storyMetadata?.challenges || '',
    // Editor configuration
    editorConfig
  };
};

/**
 * Extract planner metadata from HTML comments
 * Format: <!--@fieldName: value-->
 */
export const extractPlannerMetadata = (content) => {
  const metadata = {};
  
  // Match <!--@fieldName: value--> pattern
  const metaRegex = /<!--@(\w+):\s*([^>]*?)-->/g;
  let match;
  
  while ((match = metaRegex.exec(content)) !== null) {
    const field = match[1].toLowerCase();
    const value = match[2].trim();
    metadata[field] = value;
  }
  
  return metadata;
};

/**
 * Remove planner metadata comments from content
 * Returns clean content without metadata
 */
export const removePlannerMetadata = (content) => {
  // Remove <!--@fieldName: value--> patterns
  return content.replace(/<!--@\w+:\s*[^>]*?-->\n?/g, '');
};

/**
 * Extract links from passage content
 * Supports both formats:
 * - [[Text|Target]] (pipe - preferred)
 * - [[Text->Target]] (arrow - legacy support)
 * - [[Target]] (simple)
 */
export const extractLinks = (content) => {
  const links = [];
  
  // First remove metadata comments to avoid false matches
  const cleanContent = removePlannerMetadata(content);
  
  const linkRegex = /\[\[([^\]]+)\]\]/g;
  let match;
  
  while ((match = linkRegex.exec(cleanContent)) !== null) {
    const linkContent = match[1];
    
    // Check for pipe syntax first (preferred): [[Label|Target]]
    if (linkContent.includes('|')) {
      const [label, target] = linkContent.split('|').map(s => s.trim());
      links.push({ label, target });
    }
    // Check for arrow syntax (legacy): [[Label->Target]]
    else if (linkContent.includes('->')) {
      const [label, target] = linkContent.split('->').map(s => s.trim());
      links.push({ label, target });
    }
    // Simple syntax: [[Target]]
    else {
      const target = linkContent.trim();
      links.push({ label: target, target });
    }
  }
  
  return links;
};

/**
 * Convert graph structure back to Twee format
 * Includes planner metadata in HTML comments
 * Uses pipe syntax for links
 * @param {Object} graph - Graph with nodes and edges
 * @param {Object} options - Export options
 * @returns {string} Twee format text
 */
export const graphToTwee = (graph, options = {}) => {
  const { 
    nodes, 
    storyTitle, 
    storyVision = '', 
    storyChallenges = '',
    editorConfig = null
  } = graph;
  const { includePlannerMetadata = true } = options;
  
  let twee = '';
  
  // Add story title
  if (storyTitle) {
    twee += `:: StoryTitle\n${storyTitle}\n\n`;
  }
  
  // Add story metadata (if planner fields exist)
  if (includePlannerMetadata && (storyVision || storyChallenges || editorConfig)) {
    twee += `:: StoryData [metadata]\n`;
    if (storyVision) twee += `<!--@vision: ${storyVision}-->\n`;
    if (storyChallenges) twee += `<!--@challenges: ${storyChallenges}-->\n`;
    // Add editor config as JSON
    if (editorConfig) {
      twee += `<!--@editorConfig: ${JSON.stringify(editorConfig)}-->\n`;
    }
    twee += `\n`;
  }
  
  // Convert each node to a passage
  nodes.forEach(node => {
    // Build passage header with chapter tag
    let header = `:: ${node.name}`;
    if (node.chapterId && node.chapterId !== 'chapter-1') {
      header += ` [${node.chapterId}]`;
    }
    twee += header + '\n';
    
    // Add planner metadata as HTML comments
    if (includePlannerMetadata) {
      if (node.intention) twee += `<!--@intention: ${node.intention}-->\n`;
      if (node.purpose) twee += `<!--@purpose: ${node.purpose}-->\n`;
      if (node.dilemma) twee += `<!--@dilemma: ${node.dilemma}-->\n`;
    }
    
    // Add PathNav metadata
    if (node.url) twee += `<!--@url: ${node.url}-->\n`;
    if (node.nodeLevel) twee += `<!--@nodeLevel: ${node.nodeLevel}-->\n`;
    if (node.nodeStyle) twee += `<!--@nodeStyle: ${node.nodeStyle}-->\n`;
    if (node.isDirectUrl) twee += `<!--@isDirectUrl: true-->\n`;
    if (node.displayName && node.displayName !== node.name) {
      twee += `<!--@displayName: ${node.displayName}-->\n`;
    }
    
    // Convert arrow syntax to pipe syntax in body
    const bodyWithPipes = convertArrowsToPipes(node.body || '');
    twee += `${bodyWithPipes}\n\n`;
  });
  
  return twee.trim();
};

/**
 * Convert arrow syntax [[Text->Target]] to pipe syntax [[Text|Target]]
 */
export const convertArrowsToPipes = (content) => {
  return content.replace(/\[\[([^\]|>]+)->([^\]]+)\]\]/g, '[[$1|$2]]');
};

/**
 * Generate unique node name
 */
let nodeCounter = 1;
export const generateNodeName = () => {
  return `Passage_${nodeCounter++}`;
};

/**
 * Reset node counter
 */
export const resetNodeCounter = () => {
  nodeCounter = 1;
};
